/**
 * PlayerState — a small, shared holder for currencies and resolved stats.
 * It is NOT part of the ECS because multiple systems and the UI need to
 * poke at it each frame, and putting it in the component store would
 * require a redundant query each access.
 *
 * Systems write through the setters so events fire consistently.
 */

import { bus } from '@/core/EventBus';
import { saveManager } from '@/core/SaveManager';

export interface ResolvedStats {
  cellRadius: number;
  absorptionRadius: number;
  moveSpeedMult: number;
  atpMax: number;
  atpRegen: number;
  biomassMult: number;
  hpMax: number;
  hpRegen: number;
  /** Heritage-Tree-only damage reduction. Stacks additively with the
   *  mutation-effect damageReduction inside AbsorptionSystem. */
  metaDamageReduction: number;
}

const BASE_STATS: ResolvedStats = {
  cellRadius: 18,
  absorptionRadius: 90,
  moveSpeedMult: 1.0,
  atpMax: 100,
  atpRegen: 2,
  biomassMult: 1.0,
  hpMax: 100,
  hpRegen: 0.5,
  metaDamageReduction: 0,
};

export class PlayerState {
  biomass = 0;
  dna = 0;
  atp = 100;
  hp = 100;

  stats: ResolvedStats = { ...BASE_STATS };

  /** Heritage Tree resolver. Stages set this on init so recomputeStats
   *  can fold permanent meta-upgrades into BASE_STATS. */
  private meta: { getStat(stat: import('@/data/types').MetaStat): number } | null = null;
  setMetaUpgrades(m: { getStat(stat: import('@/data/types').MetaStat): number } | null): void {
    this.meta = m;
  }

  /** Reset the run state from a save file. */
  hydrateFromSave(): void {
    const run = saveManager.getRun();
    this.biomass = run.biomass;
    this.hp = run.hp > 0 ? run.hp : this.stats.hpMax;
    this.atp = run.atp > 0 ? run.atp : this.stats.atpMax;
    this.dna = saveManager.getMeta().dna;
  }

  /** Recompute resolved stats from upgrades + active mutation effects.
   *  Heritage Tree (`meta`) folds in BEFORE upgrades so % bonuses apply
   *  to the inflated baseline. */
  recomputeStats(effects: import('@/data/types').EffectSet, upgradeLevels: Record<string, number>,
                 upgrades: Record<string, import('@/data/types').UpgradeDef>): void {
    const s = { ...BASE_STATS };

    // --- meta (Heritage Tree) baseline shifts ---
    if (this.meta) {
      const hpMaxPct = this.meta.getStat('hpMaxBase');
      if (hpMaxPct) s.hpMax *= 1 + hpMaxPct;
      const biomassPct = this.meta.getStat('biomassMult');
      if (biomassPct) s.biomassMult *= 1 + biomassPct;
      const atpRegenAdd = this.meta.getStat('atpRegenBase');
      if (atpRegenAdd) s.atpRegen += atpRegenAdd;
      s.metaDamageReduction = this.meta.getStat('damageReduction');
    }

    // apply upgrades first. The amount per upgrade compounds — each
    // successive level grants more than the last (level^≈1.18) so a
    // heavy investment in one stat outpaces an even spread, and late
    // levels feel meaningfully different from early ones.
    //
    // cellSize is the exception: it diminishes 50% per level
    // (geometric ratio 0.5) so the cell visibly plateaus instead of
    // growing without bound. Total contribution caps at 2 × perLevel.
    for (const [id, level] of Object.entries(upgradeLevels)) {
      const def = upgrades[id];
      if (!def || level <= 0) continue;
      let amt: number;
      if (def.stat === 'cellSize') {
        // sum_{k=1..level} 0.5^(k-1) = 2 * (1 - 0.5^level)
        amt = def.perLevel * 2 * (1 - Math.pow(0.5, level));
      } else {
        const compounding = level * (1 + (level - 1) * 0.10);
        amt = def.perLevel * compounding;
      }
      switch (def.stat) {
        case 'cellSize': s.cellRadius += amt; break;
        case 'absorptionRadius': s.absorptionRadius += amt; break;
        case 'moveSpeed': s.moveSpeedMult += amt; break;
        case 'atpMax': s.atpMax += amt; break;
        case 'atpRegen': s.atpRegen += amt; break;
        case 'biomassMult': s.biomassMult += amt; break;
        case 'hpMax': s.hpMax += amt; break;
        case 'hpRegen': s.hpRegen += amt; break;
      }
    }

    // apply mutation/synergy/env effects (multiplicative for mults, additive for regens)
    if (effects.hpMaxMult) s.hpMax *= 1 + effects.hpMaxMult;
    if (effects.hpRegenMult) s.hpRegen *= 1 + effects.hpRegenMult;
    if (effects.moveSpeedMult) s.moveSpeedMult *= 1 + effects.moveSpeedMult;
    if (effects.absorptionRadiusMult) s.absorptionRadius *= 1 + effects.absorptionRadiusMult;
    if (effects.atpRegenBonus) s.atpRegen += effects.atpRegenBonus;
    if (effects.biomassMult) s.biomassMult *= 1 + effects.biomassMult;

    this.stats = s;

    // clamp current to new maxes
    this.hp = Math.min(this.hp, s.hpMax);
    this.atp = Math.min(this.atp, s.atpMax);

    bus.emit('hp:changed', { value: this.hp, max: s.hpMax });
    bus.emit('atp:changed', { value: this.atp, max: s.atpMax });
  }

  addBiomass(amount: number): void {
    this.biomass += amount;
    // Lifetime total, across every stage and run.
    if (amount > 0) saveManager.getMeta().totalBiomassEver += amount;
    bus.emit('biomass:changed', { value: this.biomass, delta: amount });
  }

  spendBiomass(amount: number): boolean {
    if (this.biomass < amount) return false;
    this.biomass -= amount;
    bus.emit('biomass:changed', { value: this.biomass, delta: -amount });
    return true;
  }

  addDna(amount: number): void {
    this.dna += amount;
    bus.emit('dna:changed', { value: this.dna, delta: amount });
  }

  spendDna(amount: number): boolean {
    if (this.dna < amount) return false;
    this.dna -= amount;
    bus.emit('dna:changed', { value: this.dna, delta: -amount });
    return true;
  }

  damage(amount: number, by: string): void {
    this.hp = Math.max(0, this.hp - amount);
    bus.emit('player:damaged', { amount, by });
    bus.emit('hp:changed', { value: this.hp, max: this.stats.hpMax });
    if (this.hp <= 0) bus.emit('player:died', undefined);
  }

  healTo(value: number): void {
    this.hp = Math.min(this.stats.hpMax, value);
    bus.emit('hp:changed', { value: this.hp, max: this.stats.hpMax });
  }

  spendAtp(amount: number): boolean {
    if (this.atp < amount) return false;
    this.atp -= amount;
    bus.emit('atp:changed', { value: this.atp, max: this.stats.atpMax });
    return true;
  }

  regenAtp(amount: number): void {
    this.atp = Math.min(this.stats.atpMax, this.atp + amount);
    bus.emit('atp:changed', { value: this.atp, max: this.stats.atpMax });
  }

  regenHp(amount: number): void {
    if (this.hp <= 0) return;
    this.hp = Math.min(this.stats.hpMax, this.hp + amount);
    bus.emit('hp:changed', { value: this.hp, max: this.stats.hpMax });
  }
}

export const player = new PlayerState();
